import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// In-memory fallback token. iOS Safari can drop localStorage written during a
// cross-site SSO redirect + reload, so we also keep the token in memory for the
// current session and fall back to it if localStorage is unavailable/cleared.
let inMemoryToken: string | null = null;
export const setAuthToken = (token: string | null) => {
  inMemoryToken = token;
};

const getToken = (): string | null => {
  try {
    return localStorage.getItem('auth_token') || inMemoryToken;
  } catch {
    return inMemoryToken;
  }
};

// Add interceptor to include auth token
apiClient.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Types
export interface Article {
  id: string;
  title: string;
  content: string;
  summary: string;
  category: string;
  keywords: string[];
  age_group: string;
  reading_time: number;
  vocabulary_level: string;
  published_date: string;

  // Hero image (Cloudflare R2 URL); undefined/null when not yet generated
  hero_image_url?: string | null;

  // Engagement features
  did_you_know?: string;
  wow_number?: string;
  picture_this?: string;
  quiz_questions?: QuizQuestion[];
  vocabulary_words?: { [key: string]: string };
  discussion_questions?: string[];
  try_this?: string;
  real_world_connection?: string;
  career_spotlight?: string;
  subject_connections?: { [key: string]: string };
  emoji_summary?: string;
  key_numbers?: string[];
}

export interface QuizQuestion {
  question: string;
  options: string[];
  correct_answer: string;
  explanation: string;
}

export interface CategoryStats {
  category: string;
  count: number;
  percentage: number;
}

// API Methods
export const articleAPI = {
  // Get articles by age group
  getArticles: async (
    ageGroup: string,
    date?: string,
    category?: string,
    page: number = 1,
    pageSize: number = 10
  ): Promise<{ articles: Article[]; total: number }> => {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
      age_group: ageGroup,
    });

    if (date) params.append('date', date);
    if (category) params.append('category', category);

    const response = await apiClient.get(`/articles/?${params}`);
    return response.data;
  },

  // Get single article
  getArticle: async (articleId: number, ageGroup: string): Promise<Article> => {
    const params = new URLSearchParams({ age_group: ageGroup });
    const response = await apiClient.get(`/articles/${articleId}?${params}`);
    return response.data;
  },

  // Get categories with stats
  getCategories: async (ageGroup?: string, date?: string): Promise<CategoryStats[]> => {
    const params = new URLSearchParams();
    if (ageGroup) params.append('age_group', ageGroup);
    if (date) params.append('date', date);

    // Use date-specific endpoint if date is provided
    const endpoint = date ? '/articles/categories/by-date' : '/categories';
    const response = await apiClient.get(`${endpoint}?${params}`);

    // Handle different response formats
    if (date && response.data.categories) {
      // Date-specific endpoint returns { date, categories, timezone, total_categories }
      return response.data.categories.map((cat: any) => ({
        category: cat.category,
        count: cat.count,
        percentage: 0 // Not provided by date-specific endpoint
      }));
    }

    return response.data;
  },

  // Get trending articles
  getTrending: async (ageGroup: string, limit: number = 5): Promise<Article[]> => {
    const response = await apiClient.get(`/trending/${ageGroup}?limit=${limit}`);
    return response.data;
  },

  // Track analytics
  trackEvent: async (articleId: string, ageGroup: string, eventType: string, value?: number) => {
    await apiClient.post('/analytics/track', {
      article_id: articleId,
      age_group: ageGroup,
      event_type: eventType,
      value,
    });
  },

  // Mark an article as read (every open counts as one read)
  markRead: async (articleId: number, ageGroup: string): Promise<void> => {
    await apiClient.post(`/articles/${articleId}/read?age_group=${encodeURIComponent(ageGroup)}`);
  },

  // Like status + toggle. The age group being read is passed through so the
  // count reflects the version on screen: staff previewing an age band have no
  // age group of their own, and without this a teacher saw the same global
  // total on all three tabs.
  getLikeStatus: async (articleId: number, ageGroup?: string): Promise<{ liked: boolean; like_count: number }> => {
    const q = ageGroup ? `?age_group=${encodeURIComponent(ageGroup)}` : '';
    const response = await apiClient.get(`/articles/${articleId}/like${q}`);
    return response.data;
  },

  toggleLike: async (articleId: number, ageGroup?: string): Promise<{ liked: boolean; like_count: number }> => {
    const q = ageGroup ? `?age_group=${encodeURIComponent(ageGroup)}` : '';
    const response = await apiClient.post(`/articles/${articleId}/like${q}`);
    return response.data;
  },
};

// Authentication Types
export interface User {
  id: number;
  email: string;
  username: string;
  full_name: string;
  role: string;
  age_group?: string;
  grade?: number;
  school?: string;
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  username: string;
  full_name: string;
  password: string;
  role?: string;
  age_group?: string;
  grade?: number;
  school?: string;
}

export interface AuthResponse {
  access_token: string;
  token_type: string;
}

// Authentication API
export const authAPI = {
  login: async (credentials: LoginRequest): Promise<AuthResponse> => {
    const response = await apiClient.post('/auth/login', {
      username: credentials.username,
      password: credentials.password,
    });

    // Store token
    localStorage.setItem('auth_token', response.data.access_token);

    return response.data;
  },

  register: async (userData: RegisterRequest): Promise<User> => {
    const response = await apiClient.post('/auth/register', userData);
    return response.data;
  },

  logout: async (): Promise<void> => {
    try {
      await apiClient.post('/auth/logout');
    } finally {
      localStorage.removeItem('auth_token');
    }
  },

  getCurrentUser: async (): Promise<User> => {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },
};

// Admin API Types
export interface BatchUploadResponse {
  message: string;
  task_id: string;
  status: string;
  category: string;
}

export interface BatchStatusResponse {
  task_id: string;
  status: string;
  result?: {
    success: boolean;
    total_images?: number;
    processed?: number;
    error?: string;
  };
}

export interface ProcessingProgress {
  total_articles: number;
  processing: number;
  published: number;
  draft: number;
  completed_percentage: number;
  token_usage: {
    total_tokens: number;
    average_tokens_per_article: number;
  };
  recent_completed: Array<{
    id: number;
    title: string;
    category: string;
    published_date: string | null;
    tokens_used: number | null;
  }>;
}

// Admin API
export const adminAPI = {
  uploadBatch: async (file: File): Promise<BatchUploadResponse> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post('/articles/upload-batch', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  },

  getBatchStatus: async (taskId: string): Promise<BatchStatusResponse> => {
    const response = await apiClient.get(`/articles/batch-status/${taskId}`);
    return response.data;
  },

  getProcessingProgress: async (): Promise<ProcessingProgress> => {
    const response = await apiClient.get('/processing-progress');
    return response.data;
  },
};

// Subscription API
export interface ExpressInterestRequest {
  preferred_plan: string;
  parent_contact_phone?: string;
  preferred_contact_time?: string;
}

export interface DeclineSubscriptionRequest {
  reason: string;
  feedback?: string;
}

export const subscriptionAPI = {
  getStatus: async () => {
    const response = await apiClient.get('/subscription/status');
    return response.data;
  },

  getPlans: async () => {
    const response = await apiClient.get('/subscription/plans');
    return response.data;
  },

  expressInterest: async (data: ExpressInterestRequest) => {
    const response = await apiClient.post('/subscription/express-interest', data);
    return response.data;
  },

  decline: async (data: DeclineSubscriptionRequest) => {
    const response = await apiClient.post('/subscription/decline', data);
    return response.data;
  },
};

// NewsFlash API
export interface NewsFlashQuestion {
  id: number;
  question_order: number;
  question_text: string;
  options: string[];
  category: string;
}

export interface NewsFlashSessionSummary {
  score: number;
  correct_count: number;
  wrong_count: number;
  skipped_count: number;
  total_questions: number;
}

export interface NewsFlashTodayResponse {
  quiz_date: string;
  age_group: string;
  total: number;
  already_attempted: boolean;
  previous_session: NewsFlashSessionSummary | null;
  questions: NewsFlashQuestion[];
}

export interface NewsFlashAnswerResponse {
  question_id: number;
  correct: boolean;
  correct_index: number;
}

export interface NewsFlashFinishResponse {
  recorded: boolean;
  session: NewsFlashSessionSummary;
}

export interface NewsFlashStartResponse {
  is_official: boolean;
  session: NewsFlashSessionSummary;
}

export const newsflashAPI = {
  // Fetch today's quiz set for an age group. Correct answers are never included.
  getToday: async (ageGroup: string, date?: string): Promise<NewsFlashTodayResponse> => {
    const params = new URLSearchParams({ age_group: ageGroup });
    if (date) params.append('date', date);
    const response = await apiClient.get(`/newsflash/today?${params}`);
    return response.data;
  },

  // Validate a single answer server-side. The correct index is returned only after answering.
  submitAnswer: async (questionId: number, selectedIndex: number): Promise<NewsFlashAnswerResponse> => {
    const response = await apiClient.post('/newsflash/answer', {
      question_id: questionId,
      selected_index: selectedIndex,
    });
    return response.data;
  },

  // Claim today's attempt (lock-on-start). is_official=false means practice.
  start: async (): Promise<NewsFlashStartResponse> => {
    const response = await apiClient.post('/newsflash/start');
    return response.data;
  },

  // Finalize today's attempt with the final (or partial) score.
  finish: async (summary: NewsFlashSessionSummary): Promise<NewsFlashFinishResponse> => {
    const response = await apiClient.post('/newsflash/finish', summary);
    return response.data;
  },
};

// Teacher/Admin Analytics API (school-scoped, staff-only on the server)
export interface AnalyticsOverview {
  school: { id: number; name: string };
  date: string;
  generated_at: string;
  students_active: number;
  articles_read: number;
  quiz_players: number;
  likes: number;
  cumulative_students: number;
  enrolled_students: number;
}

export interface ClassRow {
  class_standard: number;
  class_label: string;
  enrolled: number;
  students_active: number;
  articles_read: number;
  quiz_players: number;
  likes: number;
}

export interface AgeRow {
  age_group: string;
  label: string;
  students_active: number;
}

export interface TrendPoint {
  date: string;
  students_active: number;
  articles_read: number;
  quiz_players: number;
}

export const analyticsAPI = {
  getOverview: async (date?: string): Promise<AnalyticsOverview> => {
    const params = date ? `?date=${date}` : '';
    const res = await apiClient.get(`/teacher-analytics/overview${params}`);
    return res.data;
  },
  getByClass: async (date?: string): Promise<{ classes: ClassRow[]; school: { id: number; name: string }; date: string }> => {
    const params = date ? `?date=${date}` : '';
    const res = await apiClient.get(`/teacher-analytics/by-class${params}`);
    return res.data;
  },
  getByAge: async (date?: string): Promise<{ age_groups: AgeRow[] }> => {
    const params = date ? `?date=${date}` : '';
    const res = await apiClient.get(`/teacher-analytics/by-age${params}`);
    return res.data;
  },
  getTrend: async (days: number = 14): Promise<{ series: TrendPoint[] }> => {
    const res = await apiClient.get(`/teacher-analytics/trend?days=${days}`);
    return res.data;
  },
};

// Export apiClient as default for components that need direct access
// Leaderboard (monthly, class-wise Knoozy Points)
export interface LeaderboardEntry {
  rank: number;
  name: string;
  is_you?: boolean;
}

export interface MyLeaderboard {
  month: string;
  month_key: string;
  class_label: string;
  your_rank: number | null;
  total_students: number;
  entries: LeaderboardEntry[];
}

export interface ClassLeaderboard {
  school: { id: number; name: string };
  month: string;
  month_key: string;
  class_label: string;
  class_standard: number;
  total_students: number;
  entries: LeaderboardEntry[];
}

export const leaderboardAPI = {
  getMine: async (month?: string): Promise<MyLeaderboard> => {
    const params = month ? `?month=${month}` : '';
    const res = await apiClient.get(`/leaderboard/me${params}`);
    return res.data;
  },
  getClass: async (classStandard: number, month?: string): Promise<ClassLeaderboard> => {
    const params = new URLSearchParams({ class_standard: String(classStandard) });
    if (month) params.append('month', month);
    const res = await apiClient.get(`/leaderboard/class?${params}`);
    return res.data;
  },
};

export default apiClient;

// WebSocket for real-time updates
export const setupRealtimeUpdates = (ageGroup: string, onNewArticle: (article: Article) => void) => {
  // In production, this would be a WebSocket connection
  // For now, we'll poll every 30 seconds
  const pollInterval = setInterval(async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const { articles } = await articleAPI.getArticles(ageGroup, today);

      // Check if there are new articles (this would be handled better with WebSocket)
      const storedIds = localStorage.getItem('knoozy_article_ids');
      const currentIds = articles.map(a => a.id);

      if (storedIds) {
        const previousIds = JSON.parse(storedIds);
        const newArticles = articles.filter(a => !previousIds.includes(a.id));

        if (newArticles.length > 0) {
          newArticles.forEach(onNewArticle);
          localStorage.setItem('knoozy_article_ids', JSON.stringify(currentIds));
        }
      } else {
        localStorage.setItem('knoozy_article_ids', JSON.stringify(currentIds));
      }
    } catch (error) {
      console.error('Error polling for updates:', error);
    }
  }, 30000); // Poll every 30 seconds

  return () => clearInterval(pollInterval);
};